/**
 * Account → ElevenLabs voices. Connect a personal ElevenLabs key to add your
 * own designed/cloned voices to Table Read. Key is stored encrypted server-side
 * and never shown back. Brand: never says "AI".
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plug, Trash2, RefreshCw, KeyRound } from "lucide-react";

const CUSTOM_CATS = new Set(["generated", "cloned", "professional"]);
const CAT_LABEL = { generated: "Designed", cloned: "Cloned", professional: "Professional", premade: "Premade" };

export function ElevenLabsSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(null); // 'connect' | 'refresh' | 'disconnect'
  const [showPremade, setShowPremade] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/elevenlabs");
      setStatus(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!apiKey.trim()) { toast.error("Paste your ElevenLabs API key first."); return; }
    setBusy("connect");
    try {
      const { data } = await api.post("/integrations/elevenlabs", { api_key: apiKey.trim() });
      setStatus(data); setApiKey("");
      toast.success("ElevenLabs connected. Toggle the voices you want below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't connect.");
    } finally { setBusy(null); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post("/integrations/elevenlabs/refresh");
      setStatus(data); toast.success("Voice list refreshed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't refresh.");
    } finally { setBusy(null); }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await api.delete("/integrations/elevenlabs");
      setStatus({ connected: false });
      toast.success("ElevenLabs disconnected.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't disconnect.");
    } finally { setBusy(null); }
  };

  const toggleVoice = async (voice_id, included) => {
    setStatus((s) => ({ ...s, voices: s.voices.map((v) => (v.voice_id === voice_id ? { ...v, included } : v)) }));
    try {
      await api.put("/integrations/elevenlabs/voices", { voice_id, included });
    } catch (e) {
      setStatus((s) => ({ ...s, voices: s.voices.map((v) => (v.voice_id === voice_id ? { ...v, included: !included } : v)) }));
      toast.error("Couldn't update that voice.");
    }
  };

  const setSwitch = async (key, value) => {
    setStatus((s) => ({ ...s, [key]: value }));
    try {
      await api.put("/integrations/elevenlabs/settings", { [key]: value });
    } catch (e) {
      setStatus((s) => ({ ...s, [key]: !value }));
      toast.error("Couldn't save that setting.");
    }
  };

  const voices = status?.voices || [];
  const custom = voices.filter((v) => CUSTOM_CATS.has(v.category));
  const premade = voices.filter((v) => !CUSTOM_CATS.has(v.category));
  const includedCount = voices.filter((v) => v.included).length;

  return (
    <section className="bg-white border border-[var(--line)] rounded-md p-8 mb-6" data-testid="elevenlabs-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl mb-1">ElevenLabs voices</h2>
          <p className="text-sm text-[var(--ink-secondary)] max-w-xl">
            Add your own designed or cloned ElevenLabs voices to Table Read, alongside the built-in cast.
            Your custom voices use <strong>your</strong> ElevenLabs credits.
          </p>
        </div>
        {status?.connected && (
          <span className="shrink-0 text-xs uppercase tracking-widest px-2.5 py-1 rounded border text-[var(--ok)] bg-[var(--ok)]/10 border-[var(--ok)]/30" data-testid="el-status">
            Connected
          </span>
        )}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-[var(--ink-muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : !status?.connected ? (
        <div className="mt-6">
          <div className="rounded-md bg-[var(--canvas)] border border-[var(--line)] p-4 text-[13px] text-[var(--ink-secondary)] leading-relaxed mb-5" data-testid="el-instructions">
            <p className="font-medium text-[var(--ink-primary)] mb-1.5 flex items-center gap-1.5"><KeyRound size={14} /> Create a scoped key</p>
            In ElevenLabs go to <strong>Profile → API Keys → Create API Key</strong> and set:
            <span className="block mt-1">• <strong>Text to Speech: Access</strong> &nbsp;• <strong>Voices: Read</strong> &nbsp;• <strong>User: Read</strong></span>
            Leave everything else <strong>No Access</strong>. We store it encrypted, never display it again, and only use it to list your voices and generate your custom-voice audio.
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk_..."
              data-testid="el-connect-input"
              className="flex-1 bg-transparent border border-[var(--line)] focus:border-[var(--brand)] outline-none rounded px-3 py-2.5 text-sm font-mono"
            />
            <button onClick={connect} disabled={busy === "connect"} className="btn-brand disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="el-connect-btn">
              {busy === "connect" ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />} Connect
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6">
          {/* status row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b border-[var(--line)]">
            <div className="text-sm text-[var(--ink-secondary)]">
              Key <span className="font-mono">••••{status.last4}</span>
              {status.character_limit ? (
                <span data-testid="el-credits"> · {Math.max(0, (status.character_limit || 0) - (status.character_count || 0)).toLocaleString()} of {(status.character_limit || 0).toLocaleString()} credits left{status.tier ? ` (${status.tier})` : ""}</span>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button onClick={refresh} disabled={busy === "refresh"} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="el-refresh-btn">
                {busy === "refresh" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
              </button>
              <button onClick={disconnect} disabled={busy === "disconnect"} className="btn-ghost text-sm text-[var(--err)] inline-flex items-center gap-1.5" data-testid="el-disconnect-btn">
                <Trash2 size={13} /> Disconnect
              </button>
            </div>
          </div>

          {/* switches */}
          <div className="py-5 space-y-3 border-b border-[var(--line)]">
            <Switch
              checked={!!status.autocast_enabled}
              onChange={(v) => setSwitch("autocast_enabled", v)}
              label="Let smart casting use my voices"
              hint="Auto-cast can assign your custom voices to characters."
              testid="el-autocast-toggle"
            />
            <Switch
              checked={!!status.cowriter_enabled}
              onChange={(v) => setSwitch("cowriter_enabled", v)}
              label="Let co-writers use my voices on shared scripts"
              hint="Collaborators can hear your custom voices (billed to your credits)."
              testid="el-cowriter-toggle"
            />
          </div>

          {/* voice list */}
          <div className="pt-5">
            <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-3">
              Your voices — {includedCount} added to Table Read
            </p>
            {custom.length === 0 && premade.length === 0 ? (
              <p className="text-sm text-[var(--ink-muted)] italic">No voices found on this account.</p>
            ) : null}

            {custom.length > 0 && (
              <div className="space-y-1.5 mb-4">
                <p className="text-xs font-semibold text-[var(--ink-secondary)] mb-1">Designed & cloned</p>
                {custom.map((v) => <VoiceToggle key={v.voice_id} v={v} onToggle={toggleVoice} />)}
              </div>
            )}

            {premade.length > 0 && (
              <div>
                <button onClick={() => setShowPremade((s) => !s)} className="text-xs text-[var(--brand)] hover:underline mb-2" data-testid="el-show-premade">
                  {showPremade ? "Hide" : "Show"} premade voices ({premade.length})
                </button>
                {showPremade && (
                  <div className="space-y-1.5">
                    {premade.map((v) => <VoiceToggle key={v.voice_id} v={v} onToggle={toggleVoice} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function VoiceToggle({ v, onToggle }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 px-3 rounded hover:bg-[var(--warm-accent)] cursor-pointer">
      <span className="flex items-center gap-2 min-w-0">
        <span className="text-sm text-[var(--ink-primary)] truncate">{v.name}</span>
        <span className="text-[9px] uppercase tracking-wider text-[var(--ink-muted)] bg-[var(--warm-accent)] px-1.5 py-0.5 rounded shrink-0">
          {CAT_LABEL[v.category] || v.category}
        </span>
      </span>
      <input
        type="checkbox"
        checked={!!v.included}
        onChange={(e) => onToggle(v.voice_id, e.target.checked)}
        data-testid={`el-voice-toggle-${v.voice_id}`}
        className="accent-[var(--brand)] w-4 h-4 shrink-0"
      />
    </label>
  );
}

function Switch({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="accent-[var(--brand)] w-4 h-4 mt-0.5" />
      <div>
        <div className="text-sm font-medium text-[var(--ink-primary)]">{label}</div>
        {hint ? <div className="text-[11px] text-[var(--ink-muted)] mt-0.5">{hint}</div> : null}
      </div>
    </label>
  );
}
